import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PencilIcon } from "@heroicons/react/24/outline";
import { StopIcon } from "@heroicons/react/24/solid";
import { ChevronUpDownIcon } from "@heroicons/react/24/solid";
import initialData from "../Interview/question";

const SortableRow = ({
  item,
  index,
  selectedKeys,
  toggleSelection,
  isEditMode,
  toggleStatus,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#f0f0f0" : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} className="border-b">
      <td className="p-2 w-8">
        {isEditMode && (
          <ChevronUpDownIcon className="cursor-grab" {...listeners} />
        )}
      </td>
      <td className="p-2 w-8">
        {isEditMode && (
          <input
            type="checkbox"
            checked={selectedKeys.has(item.id)}
            onChange={() => toggleSelection(item.id)}
          />
        )}
      </td>

      {/* Status Column */}
      <td className="p-2 w-10">
        <button
          onClick={() => toggleStatus(item.id)}
          title={item.status === "complete" ? "Complete" : "In Progress"}
        >
          {item.status === "complete" ? (
            <StopIcon className="h-5 w-5 text-green-500" />
          ) : (
            <StopIcon className="h-5 w-5 text-blue-500" />
          )}
        </button>
      </td>

      <td className="p-2 w-10">{index + 1}</td>
      <td className="p-2">{item.title}</td>
      <td className="p-2">{item.question}</td>
      <td className="p-2">{item.category}</td>
      <td className="p-2">{item.type}</td>
      <td className="p-2">{item.answer}</td>
      <td className="p-2">{item.avgTime}</td>

      {/* Edit Icon Column */}
      <td className="p-2 w-8">
        <PencilIcon className="h-5 w-5 text-gray-500 cursor-pointer hover:text-blue-500" />
      </td>
    </tr>
  );
};

const DnDTable = () => {
  const [items, setItems] = useState(initialData);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [mode, setMode] = useState("view");
  const [activeId, setActiveId] = useState(null);

  const isEditMode = mode === "edit";

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const toggleSelection = (id) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleDragStart = (event) => {
    const draggedId = event.active.id;
    if (!selectedKeys.has(draggedId)) {
      setSelectedKeys(new Set([draggedId]));
    }
    setActiveId(draggedId);
  };

  const handleDragEnd = (event) => {
    const { over } = event;
    setActiveId(null);
    if (!over) return;

    const selectedIds = Array.from(selectedKeys);
    const draggedId = activeId;

    if (!selectedIds.includes(draggedId)) {
      selectedIds.length = 0;
      selectedIds.push(draggedId);
    }

    if (over.id === "trash") {
      setItems((prev) => prev.filter((item) => !selectedIds.includes(item.id)));
      setSelectedKeys(new Set());
      return;
    }

    const overIndex = items.findIndex((item) => item.id === over.id);
    const isLast = overIndex === items.length - 1;

    const itemsToMove = items.filter((item) => selectedIds.includes(item.id));
    const remainingItems = items.filter(
      (item) => !selectedIds.includes(item.id)
    );

    let insertIndex = remainingItems.findIndex((item) => item.id === over.id);
    if (insertIndex === -1 || isLast) {
      insertIndex = remainingItems.length;
    }

    const updated = [
      ...remainingItems.slice(0, insertIndex),
      ...itemsToMove,
      ...remainingItems.slice(insertIndex),
    ];

    setItems(updated);
    console.log("Updated items:", updated);
  };

  const handleDelete = () => {
    const updated = items.filter((item) => !selectedKeys.has(item.id));
    setItems(updated);
    setSelectedKeys(new Set());
    console.log("Updated items:", updated);
  };

  const toggleStatus = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: item.status === "complete" ? "in-progress" : "complete",
            }
          : item
      )
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 border-b border-gray-200 pb-2 mb-2">
        <span className="font-semibold text-sm">{items.length} Questions</span>
        <button
          onClick={() => setMode(isEditMode ? "view" : "edit")}
          className="text-sm text-blue-600 hover:underline"
        >
          {isEditMode ? "Done" : "Edit"}
        </button>
        {isEditMode && selectedKeys.size > 0 && (
          <button
            onClick={handleDelete}
            className="text-sm text-red-600 hover:underline"
          >
            Delete Selected
          </button>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <table className="w-full border-collapse">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-2 w-8">{isEditMode ? "" : null}</th>
                <th className="p-2 w-8">
                  {isEditMode && (
                    <input
                      type="checkbox"
                      checked={selectedKeys.size === items.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKeys(
                            new Set(items.map((item) => item.id))
                          );
                        } else {
                          setSelectedKeys(new Set());
                        }
                      }}
                    />
                  )}
                </th>
                <th className="p-2 w-10">Status</th>
                <th className="p-2 w-10">No.</th>
                <th className="p-2">Title</th>
                <th className="p-2">Question</th>
                <th className="p-2">Category</th>
                <th className="p-2">Type</th>
                <th className="p-2">Answer</th>
                <th className="p-2">Avg Time</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <SortableRow
                  key={item.id}
                  item={item}
                  index={index}
                  selectedKeys={selectedKeys}
                  toggleSelection={toggleSelection}
                  isEditMode={isEditMode}
                  toggleStatus={toggleStatus}
                />
              ))}
            </tbody>
          </table>
        </SortableContext>

        <DragOverlay>
          {activeId &&
            items
              .filter((item) => selectedKeys.has(item.id))
              .map((item) => (
                <div
                  key={item.id}
                  className="bg-white border p-2 shadow rounded mb-1"
                >
                  {item.title}
                </div>
              ))}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default DnDTable;
